/**
 * WO#2 — Territory resolution hook (READ-ONLY).
 *
 * Given the currently selected parcel, authoritatively resolves its full
 * continuous territory and exposes area / GP capacity / publishability /
 * unit count. Fail-closed and stale-safe:
 *   - Guard A: uses fetchAllOwnerParcels (throws on any incomplete state) →
 *     never derives capacity from a partial owner list.
 *   - Guard B: a monotonically increasing request id + AbortController ensure
 *     a slower earlier request can never overwrite the state of a newer
 *     selected parcel/owner.
 */
import { useEffect, useRef, useState } from "react";
import { fetchAllOwnerParcels } from "../lib/parcels-by-owner";
import { resolveTerritoryForParcel } from "../domain/live-territory-adapter";
import { isPublishable } from "../domain/grid-power";
import type { Territory } from "../domain/territory";

export interface SelectedParcelRef {
  id: number | string;
  owner: string;
}

export type TerritoryResolution =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "unavailable" }
  | {
      status: "resolved";
      territory: Territory;
      area: number;
      capacity: number;
      publishable: boolean;
      unitCount: number;
    };

export function useTerritoryResolution(
  parcel: SelectedParcelRef | null,
): TerritoryResolution {
  const [resolution, setResolution] = useState<TerritoryResolution>({
    status: "idle",
  });
  const requestIdRef = useRef(0);

  const parcelId = parcel?.id ?? null;
  const parcelOwner = parcel?.owner ?? null;

  useEffect(() => {
    if (parcelId == null || parcelOwner == null) {
      setResolution({ status: "idle" });
      return;
    }
    const myRequestId = ++requestIdRef.current;
    const controller = new AbortController();
    setResolution({ status: "loading" });

    (async () => {
      try {
        const ownerParcels = await fetchAllOwnerParcels(
          parcelOwner,
          controller.signal,
        );
        if (myRequestId !== requestIdRef.current) return; // superseded
        const territory = resolveTerritoryForParcel(
          ownerParcels,
          parcelId,
          parcelOwner,
        );
        if (myRequestId !== requestIdRef.current) return; // superseded
        if (!territory) {
          setResolution({ status: "unavailable" });
          return;
        }
        setResolution({
          status: "resolved",
          territory,
          area: territory.area,
          capacity: territory.capacity,
          publishable: isPublishable(territory.area),
          unitCount: territory.units.length,
        });
      } catch {
        if (controller.signal.aborted || myRequestId !== requestIdRef.current)
          return;
        setResolution({ status: "unavailable" });
      }
    })();

    return () => controller.abort();
  }, [parcelId, parcelOwner]);

  return resolution;
}
